/*#
# The store

`VirtaStore` is the client's working set: it replays the [sync log](/log/)
from a cursor, folds it in RAM, and answers every query from memory. Writes
go to the log first and are applied from what the log returns, so the store
never shows a state the log doesn't hold.

This is the implementation floor the design commits to for M1/M2: if the
active task list can't fit in a laptop's RAM and filter instantly, there are
bigger problems. Nothing smarter is built until a measured board needs it.

The store is framework-free. `observe()` tells you when tasks changed; the
[`<tosi-virta>`](/virta/) component wires that into tosijs bindings.

```js
import { MemoryLog, VirtaStore, counterIds, stepClock } from 'tosijs-virta'

const store = new VirtaStore({ log: new MemoryLog(), identity: 'demo', ids: counterIds(), clock: stepClock() })
await store.load()
const t = await store.create({ title: 'write the docs', tags: ['project:tosijs-virta', 'status:doing'] })
await store.comment(t.id, 'started')
await store.close(t.id, 'shipped')

const lines = []
for (const entry of store.transcript(t.id)) {
  lines.push(entry.type === 'group' ? `(${entry.label})` : `${entry.event.by}: ${entry.event.kind}`)
}
preview.textContent = lines.join('\n')
```
```test
test('the transcript attributes each event to the identity that wrote it', () => {
  // closing is done: the move into status:done folds into one tag-change row
  expect(preview.textContent).toBe('demo: created\ndemo: commented\n(2 tag changes)\ndemo: closed')
})
```
*/

/*{ "parent": "Reference", "order": 60 }*/

import {
  randomIds,
  wallClock,
  type Clock,
  type EventDraft,
  type EventKind,
  type EventPayloads,
  type IdSource,
  type ProjectProfile,
  type ProjectSettings,
  type Task,
  type AttachedFile,
  type Attachment,
  type Correction,
  type VirtaEvent,
  ATTRIBUTION_TASK_ID,
  projectEventId,
  textHash,
} from './model'
import { emptySettings } from './fold'
import { applyEvent, newFoldState, type FoldState } from './fold'
import { MemoryLog, replay, type SyncLog } from './log'
import { DEFAULT_BOARD } from './remote'
import { checkShowcase, type ShowcaseInput } from './showcase'
import {
  childrenOf,
  counts,
  leastComplete,
  PARENT_KEY,
  parentNumOf,
  partTags,
  statusValue,
} from './parents'
import { isEntry, planPractices } from './practices'
import {
  CONTINUED_BY_KEY,
  CONTINUES_KEY,
  MOVE_RELATIONS,
  PROJECT_KEY,
  inheritedTags,
  WORKING_KEY,
  EXCLUSIVE_KEYS,
  RELATION_KEYS,
  ARCHIVED_ON_ARRIVAL,
  ARCHIVED_TAG,
  ENDING_FOR,
  FINISHED_STATUSES,
  isFinished,
  OWNER_KEY,
  compileFilter,
  tagValues,
  TYPE_KEY,
  parseTag,
  TASK_TYPE,
  normalizeTag,
  sortTasks,
  type MoveRelation,
  type SortKey,
} from './tags'
import {
  retractions,
  transcript,
  whoWrote,
  type TranscriptEntry,
  type TranscriptOptions,
} from './transcript'

/**
 * A push the log acknowledged whose replay then failed. The events ARE in
 * the log — the caller must not retry the push (it would be a no-op by id
 * anyway) but should `sync()` once the log is reachable again. Distinct from
 * a push failure, where nothing was written.
 */
/**
 * A task must say something: a title or a description. A write that would
 * create a task with neither, or leave one with neither, is refused — the
 * board's + opens a draft rather than writing a placeholder, and an agent
 * gets `{ ok: false }` through `callVerb`.
 */
export class EmptyTaskError extends Error {
  constructor() {
    super('a task needs a title or a description')
    this.name = 'EmptyTaskError'
  }
}

const blank = (text: string | undefined): boolean => !String(text ?? '').trim()

/** Refuse a task that would have neither a title nor a description. */
/**
 * Closed means ended (owner, 2026-09-25, widened 2026-09-26): a task in a
 * finished status — done, obsolete, rejected — is closed, and
 * leaving one reopens it, whichever verb did it (setTag, tag, untag, a board
 * drag). Arriving in obsolete or rejected also archives the task
 * (nobody needs to see it again); reopening unarchives it (you want to).
 * Returns the intents with the matching closed / reopened / archived
 * appended; the task's other state is untouched.
 */
function withClosure(task: Task, intents: Intent[]): Intent[] {
  const tags = afterIntents(task, intents)
  const out = [...intents]
  const status = statusOf(tags).slice('status:'.length)
  if (
    ARCHIVED_ON_ARRIVAL.includes(status) &&
    statusOf(task.tags) !== `status:${status}` &&
    !tags.has(ARCHIVED_TAG)
  )
    out.push({
      taskId: task.id,
      kind: 'tagged',
      payload: { tag: ARCHIVED_TAG },
    })
  if (isFinished([...tags]) && task.closedAt === null)
    return [
      ...out,
      { taskId: task.id, kind: 'closed', payload: { reason: '' } },
    ]
  // only a move OUT of an ending reopens: a closed task that was never
  // given a finished status (an older import) is not reopened by a label
  if (task.closedAt !== null && isFinished(task.tags) && !isFinished([...tags]))
    return [
      ...out,
      ...(tags.has(ARCHIVED_TAG)
        ? [
            {
              taskId: task.id,
              kind: 'untagged' as const,
              payload: { tag: ARCHIVED_TAG },
            },
          ]
        : []),
      { taskId: task.id, kind: 'reopened', payload: {} },
    ]
  return out
}

const tagOf = (i: Intent): string | undefined =>
  (i.payload as { tag?: string }).tag

/** A task's tags once these intents (the ones for this task) apply. */
function afterIntents(task: Task, intents: Intent[]): Set<string> {
  const tags = new Set(task.tags)
  for (const i of intents) {
    if (i.taskId !== task.id) continue
    const tag = tagOf(i)
    if (!tag) continue
    if (i.kind === 'tagged') tags.add(tag)
    if (i.kind === 'untagged') tags.delete(tag)
  }
  return tags
}

/** How a move's comment says what the old owner keeps. */
const MOVE_WORDS: Record<Exclude<MoveRelation, 'none'>, string> = {
  interested: 'still of interest to',
  blocks: 'blocks',
  delays: 'delays',
}

const IN_PROGRESS_TAG = 'status:in-progress'
/** Statuses a task can be worked in without being "started" by it. */
const STARTED = new Set([
  IN_PROGRESS_TAG,
  'status:review',
  'status:archive',
  ...FINISHED_STATUSES.map((s) => `status:${s}`),
])
const statusOf = (tags: Iterable<string>): string =>
  [...tags].find((t) => t.startsWith('status:')) ?? ''
const workersIn = (tags: Iterable<string>): string[] =>
  [...tags].filter((t) => t.startsWith(`${WORKING_KEY}:`))

/**
 * The status rules every tag write goes through (tag, untag, setTag(s),
 * work), so no surface — the tag list, a menu, a drag, the CLI, MCP — can
 * get them half right:
 *
 * - **One value per exclusive key** (`EXCLUSIVE_KEYS`: status, priority,
 *   size, project). Adding `status:x` replaces the status a task has
 *   (board #162: tagging done left `status:ready`, so the closed task sat
 *   in ready); a task has one owner. Handing it to another `owner:`
 *   subscribes the previous one: an interested party, not dropped.
 * - **Working and in-progress imply each other** (owner, 2026-09-25):
 *   starting work on a task that isn't started (not in-progress, review,
 *   done or the archive) moves it to in-progress; moving a task into
 *   in-progress with nobody on it puts the writer on it (and, as `work`
 *   does, off anything else); moving it out of in-progress ends everyone's
 *   work on it.
 * - **Closed is done** (`withClosure`).
 */
function withStatusRules(
  task: Task,
  intents: Intent[],
  identity: string,
  others: Iterable<Task>
): Intent[] {
  const out = [...intents]
  const push = (kind: 'tagged' | 'untagged', tag: string, id = task.id) =>
    out.push({ taskId: id, kind, payload: { tag } })
  const mine = (i: Intent) => i.taskId === task.id
  // one value per exclusive key: the last one added wins
  for (const key of EXCLUSIVE_KEYS) {
    const prefix = `${key}:`
    const added = [...out]
      .reverse()
      .find(
        (i) => mine(i) && i.kind === 'tagged' && tagOf(i)?.startsWith(prefix)
      )
    if (!added) continue
    for (let k = out.length - 1; k >= 0; k--) {
      const i = out[k]!
      if (
        i !== added &&
        mine(i) &&
        i.kind === 'tagged' &&
        tagOf(i)?.startsWith(prefix)
      )
        out.splice(k, 1)
    }
    for (const t of task.tags)
      if (
        t.startsWith(prefix) &&
        t !== tagOf(added) &&
        !out.some((i) => mine(i) && i.kind === 'untagged' && tagOf(i) === t)
      )
        push('untagged', t)
  }
  const before = statusOf(task.tags)
  const tags = afterIntents(task, out)
  // handing a task to another owner keeps the last one an interested party
  const [previous] = tagValues(task.tags, OWNER_KEY)
  const [next] = tagValues([...tags], OWNER_KEY)
  if (
    previous &&
    next &&
    previous !== next &&
    !task.subscribers.includes(previous)
  )
    out.push({
      taskId: task.id,
      kind: 'subscribed',
      payload: { subscriber: previous },
    })
  if (
    workersIn(tags).length &&
    !workersIn(task.tags).length &&
    !STARTED.has(statusOf(tags))
  ) {
    // starting work starts the task
    const now = statusOf(tags)
    if (now) push('untagged', now)
    push('tagged', IN_PROGRESS_TAG)
  } else if (before === IN_PROGRESS_TAG && statusOf(tags) !== IN_PROGRESS_TAG) {
    // leaving in-progress ends the work on it
    for (const w of workersIn(tags)) push('untagged', w)
  } else if (
    statusOf(tags) === IN_PROGRESS_TAG &&
    before !== IN_PROGRESS_TAG &&
    !workersIn(tags).length &&
    identity
  ) {
    // moved into in-progress with nobody on it: the writer is
    const tag = `${WORKING_KEY}:${identity}`
    push('tagged', tag)
    for (const other of others)
      if (other.id !== task.id && other.tags.includes(tag))
        push('untagged', tag, other.id)
  }
  return withClosure(task, out)
}

/** Replace a task's `status:*` with `status:<value>` (no status: the inbox). */
function statusIntents(task: Task, value: string | null): Intent[] {
  const wanted = value === null ? null : `status:${value}`
  const intents: Intent[] = task.tags
    .filter((t) => t !== wanted && t.startsWith('status:'))
    .map((tag) => ({ taskId: task.id, kind: 'untagged', payload: { tag } }))
  if (wanted && !task.tags.includes(wanted))
    intents.push({ taskId: task.id, kind: 'tagged', payload: { tag: wanted } })
  return intents
}

/** The detail of how a task ended, where its status word is coarser: `closed:duplicate`, `closed:not-a-task`. */
const CLOSED_AS_KEY = 'closed'

function requireContent(title: string | undefined, body: string | undefined) {
  if (blank(title) && blank(body)) throw new EmptyTaskError()
}

/** A transaction that failed partway: its first `landed` groups (ops) are in the log. */
export class PartialTransactionError extends Error {
  constructor(
    public readonly landed: number,
    public readonly total: number,
    cause: unknown
  ) {
    super(
      `virta: ${landed} of ${total} op(s) were written before the push failed: ${
        cause instanceof Error ? cause.message : String(cause)
      }`
    )
  }
}

export class ReplayError extends Error {
  constructor(public readonly pushed: VirtaEvent[], cause: unknown) {
    super(
      `virta: ${
        pushed.length
      } event(s) were written but replaying the log failed: ${
        cause instanceof Error ? cause.message : String(cause)
      }`
    )
    this.name = 'ReplayError'
    this.cause = cause
  }
}

export interface StoreOptions {
  log: SyncLog
  /** The identity every write is attributed to. Locally a name; later, a token. */
  identity: string
  ids?: IdSource
  clock?: Clock
  /** The board people open for this log, for links in what verbs say (default virta's own). */
  board?: string
}

export interface CreateInput {
  title: string
  body?: string
  tags?: string[]
}

export type StoreListener = (changed: readonly Task[]) => void

/** What a writer supplies; the store adds id, at, by. */
export type Intent<K extends EventKind = EventKind> = {
  [P in K]: { taskId: string; kind: P; payload: EventPayloads[P] }
}[K]

export class VirtaStore {
  private readonly baseIdentity: string
  private acting: string | null = null
  /** Who writes are attributed to: the store's own identity, or the one a `runAs` call names. */
  get identity(): string {
    return this.acting ?? this.baseIdentity
  }
  private readonly logRef: SyncLog
  private readonly ids: IdSource
  private readonly clock: Clock
  private fold: FoldState = newFoldState()
  private eventsByTask = new Map<string, VirtaEvent[]>()
  private listeners = new Set<StoreListener>()
  private loaded = false
  private queue: Promise<unknown> = Promise.resolve()

  /** The board people open for this log: links in a brief or a verb's message point here. */
  readonly board: string

  constructor(options: StoreOptions) {
    this.board = options.board ?? DEFAULT_BOARD
    this.logRef = options.log
    this.baseIdentity = options.identity
    this.ids = options.ids ?? randomIds()
    this.clock = options.clock ?? wallClock
  }

  /**
   * Replay everything the log has past our cursor. Safe to call repeatedly and
   * concurrently: all log traffic is serialized, and an event at or below the
   * cursor is never applied twice.
   */
  load(): Promise<void> {
    return this.serialize(() => this.replayNow())
  }

  /** Alias for `load()` — the name to use for a periodic catch-up. */
  sync(): Promise<void> {
    return this.load()
  }

  /** The log this store folds; a `CachedLog` says whether it is stale. */
  get log(): SyncLog {
    return this.logRef
  }

  get cursor(): number {
    return this.fold.cursor
  }

  /** Corrections of attribution, in log order (board #2752). */
  get corrections(): readonly Correction[] {
    return this.fold.corrections
  }

  /** Who wrote an event, with any honoured correction applied (board #2752). */
  whoWrote(event: VirtaEvent): string {
    return whoWrote(event, this.fold.corrections)
  }

  /** Every task the fold knows, in no particular order. */
  get tasks(): Task[] {
    return Array.from(this.fold.tasks.values())
  }

  get size(): number {
    return this.fold.tasks.size
  }

  get(id: string): Task | undefined {
    return this.fold.tasks.get(id)
  }

  byNum(num: number): Task | undefined {
    for (const task of this.fold.tasks.values())
      if (task.num === num) return task
    return undefined
  }

  /** Tasks matching a [filter](/tags/), sorted. */
  /**
   * The tasks a filter matches, sorted (`updated` by default). `sort: null`
   * skips the sort and returns log order — for a caller that sorts the
   * result itself, like the board's lanes (board #36).
   */
  query(filter = '', sort: SortKey | null = 'updated'): Task[] {
    const test = compileFilter(filter)
    const hits: Task[] = []
    for (const task of this.fold.tasks.values()) if (test(task)) hits.push(task)
    return sort === null ? hits : sortTasks(hits, sort)
  }

  /** The unfolded event stream for one task, in log order. */
  events(taskId: string): VirtaEvent[] {
    return this.eventsByTask.get(taskId)?.slice() ?? []
  }

  transcript(taskId: string, options?: TranscriptOptions): TranscriptEntry[] {
    return transcript(this.events(taskId), options)
  }

  observe(listener: StoreListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  // ── verbs ────────────────────────────────────────────────────────────────

  async create(input: CreateInput): Promise<Task> {
    requireContent(input.title, input.body)
    const taskId = this.ids()
    const tags = Array.from(
      new Set((input.tags ?? []).map(normalizeTag).filter(Boolean))
    )
    const [event] = await this.commit([
      {
        taskId,
        kind: 'created',
        payload: { title: input.title, body: input.body ?? '', tags },
      },
    ])
    return this.fold.tasks.get(event!.taskId)!
  }

  /**
   * A continuation: a new task that carries on from `taskId`, linked both
   * ways (`continues:#n` / `continued-by:#m`) and inheriting the obvious tags
   * (project, kind, area, priority) — never state. One commit: the new
   * task's number is known inside the queue, so the back-link is exact.
   */
  async continueFrom(
    taskId: string,
    input: { title?: string; body?: string; tags?: string[] } = {}
  ): Promise<Task> {
    const source = this.require(taskId)
    requireContent(input.title ?? source.title, input.body)
    const newId = this.ids()
    await this.commit(() => {
      const from = this.require(taskId)
      const num = this.fold.nextNum
      const tags = Array.from(
        new Set([
          ...inheritedTags(from.tags),
          ...(input.tags ?? []).map(normalizeTag).filter(Boolean),
          `${CONTINUES_KEY}:#${from.num}`,
        ])
      )
      return [
        {
          taskId: newId,
          kind: 'created',
          payload: {
            title: input.title ?? from.title,
            body: input.body ?? '',
            tags,
          },
        },
        {
          taskId,
          kind: 'tagged',
          payload: { tag: `${CONTINUED_BY_KEY}:#${num}` },
        },
        {
          taskId,
          kind: 'commented',
          payload: { text: `continued in #${num}` },
        },
      ]
    })
    return this.fold.tasks.get(newId)!
  }

  async retitle(taskId: string, title: string): Promise<VirtaEvent[]> {
    requireContent(title, this.require(taskId).body)
    return this.commit([{ taskId, kind: 'retitled', payload: { title } }])
  }

  /**
   * Replace a task's body. The edit says which body it replaced (its
   * fingerprint, board #2473): made from an older one — another writer got
   * there first — it is kept beside the body and flagged, not lost. An edit
   * made from the current body settles any flagged ones: that's how a
   * conflict is resolved (choose one, or merge, and save).
   */
  async edit(
    taskId: string,
    body: string,
    note?: string
  ): Promise<VirtaEvent[]> {
    requireContent(this.require(taskId).title, body)
    return this.commit(() => {
      const task = this.require(taskId)
      // unchanged, and nothing to settle: nothing to write
      if (task.body === body && !task.conflicts.length) return []
      return [
        {
          taskId,
          kind: 'edited',
          payload: {
            body,
            base: textHash(task.body),
            ...(note?.trim() ? { note: note.trim() } : {}),
          },
        },
      ]
    })
  }

  /** Add tags; ones the task already carries are skipped, not re-logged. */
  tag(taskId: string, ...tags: string[]): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() => {
      const task = this.require(taskId)
      const intents: Intent[] = []
      for (const raw of tags) {
        const tag = normalizeTag(raw)
        if (
          tag &&
          !task.tags.includes(tag) &&
          !intents.some((i) => (i.payload as { tag?: string }).tag === tag)
        ) {
          intents.push({ taskId, kind: 'tagged', payload: { tag } })
        }
      }
      return this.statusRules(task, intents)
    })
  }

  untag(taskId: string, ...tags: string[]): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() => {
      const task = this.require(taskId)
      const intents: Intent[] = []
      for (const raw of tags) {
        const tag = normalizeTag(raw)
        if (task.tags.includes(tag))
          intents.push({ taskId, kind: 'untagged', payload: { tag } })
      }
      return this.statusRules(task, intents)
    })
  }

  /** Replace every `key:*` tag with `key:value` — the move-between-lanes verb. */
  setTag(
    taskId: string,
    key: string,
    value: string | null
  ): Promise<VirtaEvent[]> {
    return this.setTags(taskId, { [key]: value })
  }

  /**
   * Several keys at once — `{ status: 'ready', priority: 'high' }` — as one
   * commit: each `key:*` is replaced by `key:value` (null clears the key).
   * Triage is this, task after task (board #148). Closed-is-done applies to
   * the result as a whole.
   */
  setTags(
    taskId: string,
    values: Record<string, string | null>
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() => {
      const task = this.require(taskId)
      const intents: Intent[] = []
      for (const [key, value] of Object.entries(values)) {
        const wanted = value === null ? null : `${key}:${value}`
        for (const tag of task.tags)
          if (tag !== wanted && tag.startsWith(`${key}:`))
            intents.push({ taskId, kind: 'untagged', payload: { tag } })
        if (wanted && !task.tags.includes(wanted))
          intents.push({ taskId, kind: 'tagged', payload: { tag: wanted } })
      }
      return this.statusRules(task, intents)
    })
  }

  /**
   * Hand a task to another project: its one owner changes. `relation` is
   * what the project it leaves keeps: `interested` (the default — it still
   * sees the task on its board), `blocks` / `delays` (it is waiting on it),
   * or `none`. One commit: retag, provenance (`from:<old>`), the relation,
   * and a comment. A relation the new owner held on the task goes: a
   * project is not a party to its own task.
   */
  move(
    taskId: string,
    project: string,
    relation: MoveRelation = 'interested'
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    const target = normalizeTag(project)
    if (!target) throw new Error('move needs a project name')
    if (!MOVE_RELATIONS.includes(relation))
      throw new Error(`unknown relation "${relation}"`)
    return this.commit(() => {
      const task = this.require(taskId)
      const from = tagValues(task.tags, PROJECT_KEY)[0] ?? ''
      if (from === target) return []
      const intents: Intent[] = []
      const untag = (tag: string) =>
        intents.push({ taskId, kind: 'untagged', payload: { tag } })
      const tag = (tag: string) => {
        if (!task.tags.includes(tag))
          intents.push({ taskId, kind: 'tagged', payload: { tag } })
      }
      for (const t of task.tags)
        if (
          t.startsWith(`${PROJECT_KEY}:`) ||
          RELATION_KEYS.some((key) => t === `${key}:${target}`)
        )
          untag(t)
      tag(`${PROJECT_KEY}:${target}`)
      let text = `moved to ${target}`
      if (from) {
        tag(`from:${from}`)
        text = `moved from ${from} to ${target}`
        if (relation !== 'none') {
          tag(`${relation}:${from}`)
          text += ` — ${MOVE_WORDS[relation]} ${from}`
        }
      }
      intents.push({ taskId, kind: 'commented', payload: { text } })
      return intents
    })
  }

  comment(taskId: string, text: string): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit([{ taskId, kind: 'commented', payload: { text } }])
  }

  /**
   * Archive several tasks in one commit (board #766): "don't look at them",
   * nothing else changes. Already archived ones are skipped; unarchive is
   * \`untag #n archived\`.
   */
  archive(taskIds: string[]): Promise<VirtaEvent[]> {
    for (const id of taskIds) this.require(id)
    return this.commit(() =>
      taskIds
        .filter((id) => !this.require(id).tags.includes(ARCHIVED_TAG))
        .map((taskId) => ({
          taskId,
          kind: 'tagged' as const,
          payload: { tag: ARCHIVED_TAG },
        }))
    )
  }

  /**
   * Take back one of your comments (board #759): it stays in the log and
   * the transcript, marked retracted, its text still there for a reply
   * that depends on it. With no \`comment\` id, your latest comment on the
   * task (the "oops" case); \`undo\` puts a retracted one back. Only the
   * author can: a retraction of someone else's comment is refused here,
   * and the transcript ignores one that reaches the log anyway.
   */
  retract(
    taskId: string,
    options: { comment?: string; reason?: string; undo?: boolean } = {}
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() => {
      const events = this.events(taskId)
      const state = retractions(events)
      const undo = !!options.undo
      const mine = events.filter(
        (e) => e.kind === 'commented' && e.by === this.identity
      )
      let target: VirtaEvent | undefined
      if (options.comment) {
        target = events.find(
          (e) => e.id === options.comment && e.kind === 'commented'
        )
        if (!target)
          throw new Error(`no comment ${options.comment} on this task`)
        if (target.by !== this.identity)
          throw new Error(
            `only its author can retract a comment (it is ${target.by}'s)`
          )
      } else {
        target = [...mine].reverse().find((e) => state.has(e.id) === undo)
        if (!target)
          throw new Error(
            undo
              ? 'you have no retracted comment on this task to restore'
              : 'you have no comment on this task to retract'
          )
      }
      if (state.has(target.id) === !undo) return []
      return [
        {
          taskId,
          kind: 'retracted',
          payload: {
            commentId: target.id,
            reason: options.reason ?? '',
            ...(undo ? { undo: true } : {}),
          },
        },
      ]
    })
  }

  /**
   * Give a finished task its showcase, or replace it (board #2439): only a
   * closed task has one — close it first. `withdraw` takes it down. A
   * showcase that breaks a rule (no text, over 200 characters, a URL that
   * isn't http(s)) is refused with the fix, never trimmed quietly.
   */
  showcase(
    taskId: string,
    input: ShowcaseInput & { withdraw?: boolean }
  ): Promise<VirtaEvent[]> {
    const task = this.require(taskId)
    if (input.withdraw)
      return this.commit(() =>
        this.require(taskId).showcase
          ? [
              {
                taskId,
                kind: 'showcased',
                payload: {
                  title: '',
                  text: '',
                  media: '',
                  link: '',
                  withdrawn: true,
                },
              },
            ]
          : []
      )
    if (task.closedAt === null)
      throw new Error(
        `#${task.num} is still open: a showcase is for finished work — close it first`
      )
    const checked = checkShowcase(input, task.title)
    if (!checked.ok) throw new Error(checked.why)
    const { title, text, media, alt, link, file } = checked
    return this.commit(() => {
      const now = this.require(taskId).showcase
      // the same showcase again is nothing to write
      if (
        now &&
        now.title === title &&
        now.text === text &&
        now.media === media &&
        now.alt === alt &&
        now.link === link &&
        now.file?.path === file?.path
      )
        return []
      return [
        {
          taskId,
          kind: 'showcased',
          payload: { title, text, media, alt, link, ...(file ? { file } : {}) },
        },
      ]
    })
  }

  /** Whether the log holds an event with this id (a planner's "already done"). */
  holds(eventId: string): boolean {
    return this.fold.seen.has(eventId)
  }

  /**
   * What a practice does when it fires (board #2474): tasks matching `when`
   * (a filter) get the tags in `set`. Only a practice or policy has one.
   * A procedure never ends a task, starts work or archives: those stay a
   * person's (a set with status:done, working: or archived is refused).
   */
  procedure(
    taskId: string,
    input: { when: string; set: string[] }
  ): Promise<VirtaEvent[]> {
    const task = this.require(taskId)
    const type = tagValues(task.tags, TYPE_KEY)[0] ?? TASK_TYPE
    if (type !== 'practice' && type !== 'policy')
      throw new Error(
        `#${task.num} is a ${type}: a procedure is for a practice or a policy (set type:practice first)`
      )
    const set = input.set.map((t) => normalizeTag(t)).filter(Boolean)
    if (!set.length)
      throw new Error('a procedure sets at least one tag: --set kind:bug')
    for (const tag of set) {
      const { key, value } = parseTag(tag)
      if (
        (key === 'status' && value !== null && isFinished([tag])) ||
        key === WORKING_KEY ||
        tag === ARCHIVED_TAG ||
        key === TYPE_KEY
      )
        throw new Error(
          `a procedure can't set ${tag}: ending, starting or archiving a task, or changing what it is, stays a person's`
        )
    }
    const when = input.when.trim()
    return this.commit(() => {
      const now = this.require(taskId).procedure
      if (now && now.when === when && now.set.join(' ') === set.join(' '))
        return []
      return [{ taskId, kind: 'procedure', payload: { when, set } }]
    })
  }

  /**
   * A person resolves a practice's work on a task (board #2474): accept a
   * suggestion (its tags are set, as this writer), overrule it, or undo an
   * automatic change (exactly what it added comes off, what it removed
   * goes back). Each is recorded — the evidence the practice's measure
   * reads.
   */
  resolve(
    taskId: string,
    of: string,
    outcome: 'accepted' | 'overruled' | 'undone'
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() => {
      const task = this.require(taskId)
      const events = this.events(taskId)
      if (events.some((e) => e.kind === 'resolved' && e.payload.of === of))
        return []
      const made = events.find((e) => e.id === of)
      if (outcome === 'undone') {
        if (!made || made.kind !== 'practiced')
          throw new Error(`${of} isn't a practice's change on #${task.num}`)
        const p = made.payload
        return this.statusRules(task, [
          ...p.added
            .filter((t) => task.tags.includes(t))
            .map((tag) => ({
              taskId,
              kind: 'untagged' as const,
              payload: { tag },
            })),
          ...p.removed
            .filter((t) => !task.tags.includes(t))
            .map((tag) => ({
              taskId,
              kind: 'tagged' as const,
              payload: { tag },
            })),
          { taskId, kind: 'resolved', payload: { of, outcome } },
        ])
      }
      const pending = task.pending.find((s) => s.id === of)
      if (!pending)
        throw new Error(`no suggestion ${of} waiting on #${task.num}`)
      if (outcome === 'overruled')
        return [{ taskId, kind: 'resolved', payload: { of, outcome } }]
      return this.statusRules(task, [
        ...pending.set
          .filter((t) => !task.tags.includes(t))
          .map((tag) => ({
            taskId,
            kind: 'tagged' as const,
            payload: { tag },
          })),
        { taskId, kind: 'resolved', payload: { of, outcome } },
      ])
    })
  }

  /**
   * Run the practices (board #2474): what `planPractices` finds, written in
   * one push with the ids it made — so a second run, or another machine's,
   * writes nothing again. `dry` plans only.
   */
  async runPractices(
    options: { project?: string; dry?: boolean } = {}
  ): Promise<{ applications: number; lines: string[]; dry: boolean }> {
    const plan = planPractices(this, { ...options, runner: this.identity })
    if (!options.dry && plan.drafts.length) await this.import(plan.drafts)
    return {
      applications: plan.lines.length,
      lines: plan.lines,
      dry: options.dry === true,
    }
  }

  /**
   * How a practice is judged (board #2472): its success measure and its
   * death condition. Only a practice or a policy has one; replacing it is
   * a new event (a new version — evidence stays tagged with the one it was
   * collected under, #2476), and the same one again writes nothing.
   */
  measure(
    taskId: string,
    input: { measure: string; death: string }
  ): Promise<VirtaEvent[]> {
    const task = this.require(taskId)
    const type = tagValues(task.tags, TYPE_KEY)[0] ?? TASK_TYPE
    if (type !== 'practice' && type !== 'policy')
      throw new Error(
        `#${task.num} is a ${type}: a measure is for a practice or a policy (set type:practice first)`
      )
    const measure = input.measure.trim()
    const death = input.death.trim()
    if (!measure || !death)
      throw new Error(
        'a practice is judged by a measure AND a death condition (what result means retire or revise): give both'
      )
    return this.commit(() => {
      const now = this.require(taskId).measure
      if (now && now.measure === measure && now.death === death) return []
      return [{ taskId, kind: 'measured', payload: { measure, death } }]
    })
  }

  /**
   * Attach a link, or a file already stored in a storage area (board
   * #2436): the event names it; the bytes live in the area.
   */
  attach(
    taskId: string,
    name: string,
    url: string,
    file?: AttachedFile
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit([
      {
        taskId,
        kind: 'attached',
        payload: file ? { name, url: '', file } : { name, url },
      },
    ])
  }

  /**
   * What a task carries (board #3560): each attachment, oldest first, with
   * the stored file's reference (a file) or its url (a link). `name`
   * narrows to the attachments of that name.
   */
  attachments(taskId: string, name = ''): Attachment[] {
    this.require(taskId)
    const out: Attachment[] = []
    for (const e of this.events(taskId)) {
      if (e.kind !== 'attached' || (name && e.payload.name !== name)) continue
      out.push({
        name: e.payload.name,
        ...(e.payload.file ? { file: e.payload.file } : { url: e.payload.url }),
        at: e.at,
        by: this.whoWrote(e),
      })
    }
    return out
  }

  /**
   * Close a task: it has ended. One commit: the task moves to the status it
   * ended in, nobody is working on it any more, and it closes. `as` says how
   * (board #149, #753): nothing or `done` is work finished; `obsolete`
   * (and `superseded`, `duplicate`, `absorbed`) and `rejected` (and
   * `not-a-task`) end in those statuses and archive the task. Where the
   * word is finer than the status (`superseded`), `closed:<as>` keeps it.
   * Whether a finished task goes to `status:review` first is the writer's
   * call — the tool never enforces a workflow.
   */
  /**
   * File a part of a parent (board #755): a new task with the parent's
   * project, kind, area and priority, tagged `parent:#n`. The parent then
   * follows it, like any other part.
   */
  child(
    parentId: string,
    input: { title: string; body?: string; tags?: string[] }
  ): Promise<Task> {
    // by #n or by id, like every reference an agent types
    const m = /^#?(\d+)$/.exec(parentId.trim())
    const parent = (m && this.byNum(Number(m[1]))) || this.require(parentId)
    return this.create({
      ...input,
      tags: [...partTags(parent), ...(input.tags ?? [])],
    })
  }

  /**
   * A parent's unfinished parts (board #755): the counting ones that aren't
   * done, down through nesting to the tasks with no unfinished parts of
   * their own — the ones a move actually moves.
   */
  unfinishedParts(parentId: string): Task[] {
    const out: Task[] = []
    const walk = (t: Task, depth: number) => {
      for (const k of childrenOf(this.fold.tasks.values(), t)) {
        const status = statusValue(k.tags)
        if (!counts(status) || status === 'done') continue
        const before = out.length
        if (depth < 50) walk(k, depth + 1)
        // a part with unfinished parts of its own follows them; a leaf moves
        if (out.length === before) out.push(k)
      }
    }
    walk(this.require(parentId), 0)
    return out
  }

  /**
   * Move a parent by moving its unfinished parts (board #755; owner,
   * 2026-10-06: a parent doesn't drag its parts, but setting it to the
   * freezer should offer to move them there). Every unfinished part goes
   * to \`status\` in one commit, and the parent follows them, as it follows
   * any move. An ending (done, obsolete, rejected) closes them.
   */
  async moveParts(parentId: string, status: string): Promise<Task[]> {
    const m = /^#?(\d+)$/.exec(parentId.trim())
    const parent = (m && this.byNum(Number(m[1]))) || this.require(parentId)
    const parts = this.unfinishedParts(parent.id)
    if (!parts.length)
      throw new Error(
        `#${parent.num} has no unfinished parts to move: it is as complete as they are`
      )
    const value = status === 'inbox' ? '' : status.trim()
    const ending = (FINISHED_STATUSES as readonly string[]).includes(value)
    await this.transaction(async (scratch) => {
      for (const part of parts) {
        if (ending)
          await scratch.close(
            part.id,
            `with its parent #${parent.num}`,
            value === 'done' ? '' : value
          )
        else await scratch.setTag(part.id, 'status', value || null)
      }
    })
    return parts.map((p) => this.require(p.id))
  }

  /**
   * File a parent for an existing task (board #755; owner, 2026-10-06): a
   * new task with the child's project, kind, area and priority, and the
   * child made its part — one commit, so the parent arrives at its part's
   * status (a parent for an item already in progress is in progress).
   */
  async parentFor(
    childId: string,
    input: { title: string; body?: string; tags?: string[] }
  ): Promise<Task> {
    const m = /^#?(\d+)$/.exec(childId.trim())
    const child = (m && this.byNum(Number(m[1]))) || this.require(childId)
    let id = ''
    await this.transaction(async (scratch) => {
      const parent = await scratch.create({
        ...input,
        tags: [
          ...child.tags.filter((t) => /^(project|kind|area|priority):/.test(t)),
          ...(input.tags ?? []),
        ],
      })
      id = parent.id
      await scratch.tag(child.id, `${PARENT_KEY}:#${parent.num}`)
    })
    return this.require(id)
  }

  close(taskId: string, reason = '', as = ''): Promise<VirtaEvent[]> {
    const target = this.require(taskId)
    const word = normalizeTag(as)
    // a practice isn't done, it's retired — and says why (board #2496)
    if (isEntry(target) && target.closedAt === null) {
      if (!word || word === 'done')
        throw new Error(
          `#${target.num} is a ${
            tagValues(target.tags, TYPE_KEY)[0]
          }: it isn't done, it's retired — close it --as retired "why" (or split, supersede or merge it)`
        )
      if (!reason.trim())
        throw new Error(
          `retiring #${target.num} needs the reason: close --as ${word} "why"`
        )
    }
    const ending = word ? ENDING_FOR[word] : 'done'
    if (!ending || !Object.hasOwn(ENDING_FOR, word || 'done'))
      throw new Error(
        `close as "${as}"? how a task ends: ${Object.keys(ENDING_FOR).join(
          ', '
        )}`
      )
    const how =
      word && word !== ending ? normalizeTag(`${CLOSED_AS_KEY}:${word}`) : ''
    return this.commit(() => {
      const task = this.require(taskId)
      if (task.closedAt !== null) return []
      return [
        ...statusIntents(task, ending),
        ...(ARCHIVED_ON_ARRIVAL.includes(ending) &&
        !task.tags.includes(ARCHIVED_TAG)
          ? [
              {
                taskId,
                kind: 'tagged' as const,
                payload: { tag: ARCHIVED_TAG },
              },
            ]
          : []),
        ...task.tags
          .filter((t) => t.startsWith(`${WORKING_KEY}:`))
          .map((tag) => ({
            taskId,
            kind: 'untagged' as const,
            payload: { tag },
          })),
        ...(how && !task.tags.includes(how)
          ? [{ taskId, kind: 'tagged' as const, payload: { tag: how } }]
          : []),
        { taskId, kind: 'closed', payload: { reason } },
      ]
    })
  }

  /**
   * Hand finished work to a person to look at (board #166): say what
   * changed and what to check, move the task to `status:review`, and take
   * everyone off it — one commit, the counterpart of `close` for work that
   * needs a look before it counts.
   */
  review(taskId: string, note: string): Promise<VirtaEvent[]> {
    this.require(taskId)
    if (blank(note))
      throw new Error('review needs a note: what changed, and what to check')
    return this.commit(() => {
      const task = this.require(taskId)
      if (task.closedAt !== null)
        throw new Error(`#${task.num} is closed: reopen it first`)
      const intents: Intent[] = [
        { taskId, kind: 'commented', payload: { text: note } },
        ...statusIntents(task, 'review'),
        ...workersIn(task.tags).map((tag) => ({
          taskId,
          kind: 'untagged' as const,
          payload: { tag },
        })),
      ]
      return this.statusRules(task, intents)
    })
  }

  /** Reopen a task: out of its ending and back to ready, unarchived; how it ended no longer applies. */
  reopen(taskId: string): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() => {
      const task = this.require(taskId)
      if (task.closedAt === null) return []
      return [
        ...(isFinished(task.tags) ? statusIntents(task, 'ready') : []),
        ...task.tags
          .filter(
            (t) => t.startsWith(`${CLOSED_AS_KEY}:`) || t === ARCHIVED_TAG
          )
          .map((tag) => ({
            taskId,
            kind: 'untagged' as const,
            payload: { tag },
          })),
        { taskId, kind: 'reopened', payload: {} },
      ]
    })
  }

  /**
   * Run `work` with every write attributed to `identity` — an MCP call that
   * says who it acts as (board #171). The caller runs one at a time: the
   * identity is the store's for the duration, not a per-write argument.
   */
  async runAs<T>(identity: string, work: () => Promise<T>): Promise<T> {
    if (this.acting !== null)
      throw new Error('runAs does not nest: one call at a time')
    this.acting = identity
    try {
      return await work()
    } finally {
      this.acting = null
    }
  }

  subscribe(taskId: string, subscriber = this.identity): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() =>
      this.require(taskId).subscribers.includes(subscriber)
        ? []
        : [{ taskId, kind: 'subscribed', payload: { subscriber } }]
    )
  }

  unsubscribe(
    taskId: string,
    subscriber = this.identity
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    return this.commit(() =>
      this.require(taskId).subscribers.includes(subscriber)
        ? [{ taskId, kind: 'unsubscribed', payload: { subscriber } }]
        : []
    )
  }

  /**
   * Say who is working on a task. One thing at a time: the same identity's
   * `working:` tag comes off every other task in the same commit — unless
   * `also` is set, which is the explicit "as well as" the design allows.
   */
  work(
    taskId: string,
    options: { also?: boolean; worker?: string } = {}
  ): Promise<VirtaEvent[]> {
    this.require(taskId)
    const worker = options.worker ?? this.identity
    const tag = `${WORKING_KEY}:${worker}`
    return this.commit(() => {
      const intents: Intent[] = []
      if (!options.also) {
        for (const other of this.fold.tasks.values()) {
          if (other.id !== taskId && other.tags.includes(tag)) {
            intents.push({
              taskId: other.id,
              kind: 'untagged',
              payload: { tag },
            })
          }
        }
      }
      const task = this.require(taskId)
      if (!task.tags.includes(tag))
        intents.push({ taskId, kind: 'tagged', payload: { tag } })
      return this.statusRules(task, intents)
    })
  }

  /** `withStatusRules` for this store's writer and its tasks. */
  private statusRules(task: Task, intents: Intent[]): Intent[] {
    return withStatusRules(
      task,
      intents,
      this.identity,
      this.fold.tasks.values()
    )
  }

  /** Stop working on one task, or on everything if no task is named. */
  stopWork(taskId?: string, worker = this.identity): Promise<VirtaEvent[]> {
    if (taskId) this.require(taskId)
    const tag = `${WORKING_KEY}:${worker}`
    return this.commit(() => {
      const intents: Intent[] = []
      const targets = taskId
        ? [this.require(taskId)]
        : Array.from(this.fold.tasks.values())
      for (const task of targets) {
        if (task.tags.includes(tag))
          intents.push({ taskId: task.id, kind: 'untagged', payload: { tag } })
      }
      return intents
    })
  }

  /**
   * Every project the board knows: one entry per `project:` tag value seen
   * on any task, plus any project whose settings were set, with the count
   * of open tasks. Profiles without settings are the empty defaults.
   */
  get projects(): (ProjectProfile & { open: number; total: number })[] {
    const counts = new Map<string, { open: number; total: number }>()
    for (const task of this.fold.tasks.values()) {
      for (const name of tagValues(task.tags, 'project')) {
        const c = counts.get(name) ?? { open: 0, total: 0 }
        c.total++
        if (task.closedAt === null) c.open++
        counts.set(name, c)
      }
    }
    const names = new Set([...counts.keys(), ...this.fold.projects.keys()])
    return Array.from(names)
      .sort()
      .map((name) => ({
        ...this.project(name),
        ...(counts.get(name) ?? { open: 0, total: 0 }),
      }))
  }

  /** A project's profile: its settings, or the empty defaults. */
  project(name: string): ProjectProfile {
    return (
      this.fold.projects.get(name) ?? {
        ...emptySettings(),
        name,
        updatedAt: '',
        updatedBy: '',
      }
    )
  }

  /**
   * A new task's tags when it is filed from `repo` (boards #1082, #2431): a
   * task that names no project belongs to the repo it was filed from, when
   * that repo is enrolled; `orphan` says it is on no board. The CLI and the
   * MCP server both ask this — one rule for every surface that creates.
   */
  homeFor(
    tags: readonly string[],
    repo: string
  ): { tags: string[]; orphan: boolean } {
    if (tags.some((t) => t.startsWith('project:')))
      return { tags: [...tags], orphan: false }
    if (repo && this.project(repo).enrolled)
      return { tags: [...tags, `project:${repo}`], orphan: false }
    return { tags: [...tags], orphan: true }
  }

  /**
   * A project adopts the board (board #1121): records when and by whom.
   * Self-service — \`virta onboard\` calls it for the repo it runs in, and
   * nothing else does; an ask filed against a repo, a move, an import or a
   * notes file never enrolls it. Already enrolled: nothing is written.
   */
  enroll(name: string, at = new Date().toISOString()): Promise<VirtaEvent[]> {
    if (this.project(name).enrolled) return Promise.resolve([])
    return this.setProject(name, {
      enrolled: `${at.slice(0, 10)} by ${this.identity}`,
    })
  }

  /** Set some of a project's settings (an upsert; unchanged keys are skipped). */
  /**
   * Correct who a credential says it is (board #2752), by appending: the
   * events stamped by `token` (through `through`, else from now on too), or
   * the listed `events`, are shown as `label`. The log is untouched; the
   * correction is itself attributed, and only counts when this store's
   * credential is the same token, or its principal signed in directly.
   */
  reattribute(input: {
    label?: string
    agent?: string
    token?: string
    events?: string[]
    through?: string
    reason?: string
  }): Promise<VirtaEvent[]> {
    const label = (input.label ?? '').trim()
    const agent = (input.agent ?? '').trim()
    if (!label && !agent)
      throw new Error('say what to correct: a label, an agent, or both')
    if (label && label.length < 3)
      throw new Error('a corrected label needs at least 3 characters')
    if (agent.includes('×'))
      throw new Error('the agent part is one name, without ×')
    // a token writes for every repo on its machine: an agent correction
    // names the writes it fixes, never a whole token
    if (agent && !input.events?.length)
      throw new Error(
        'an agent correction names its events (a token writes for every repo on its machine): pass events, or --seq in the CLI'
      )
    if (!input.token && !input.events?.length)
      throw new Error('say what it corrects: a token, or events')
    if (
      input.through !== undefined &&
      !Number.isFinite(Date.parse(input.through))
    )
      throw new Error(`through ${input.through}: an ISO time`)
    return this.commit(() => [
      {
        taskId: ATTRIBUTION_TASK_ID,
        kind: 'reattributed',
        payload: {
          ...(label ? { label } : {}),
          ...(agent ? { agent } : {}),
          ...(input.token ? { token: input.token } : {}),
          ...(input.events?.length ? { events: input.events } : {}),
          ...(input.through ? { through: input.through } : {}),
          ...(input.reason ? { reason: input.reason } : {}),
        },
      },
    ])
  }

  /** How many events a token's stamps are on (through `through`): what a correction would reach. */
  stampedBy(token: string, through?: string): number {
    let n = 0
    for (const id of [...this.fold.tasks.keys()])
      for (const e of this.events(id))
        if (e.attribution?.token === token && (!through || e.at <= through)) n++
    return n
  }

  setProject(
    name: string,
    settings: Partial<ProjectSettings>
  ): Promise<VirtaEvent[]> {
    return this.commit(() => {
      const current = this.project(name)
      const changed: Partial<ProjectSettings> = {}
      for (const [key, value] of Object.entries(settings)) {
        if (
          typeof value === 'string' &&
          current[key as keyof ProjectSettings] !== value
        )
          changed[key as keyof ProjectSettings] = value
      }
      if (Object.keys(changed).length === 0) return []
      return [
        {
          taskId: projectEventId(name),
          kind: 'project',
          payload: { name, settings: changed },
        },
      ]
    })
  }

  /** What an identity is working on right now (normally zero or one task). */
  workingOn(worker = this.identity): Task[] {
    const tag = `${WORKING_KEY}:${worker}`
    return this.tasks.filter((t) => t.tags.includes(tag))
  }

  /**
   * Write already-formed drafts (seeds, migrations, adapters). Attribution and
   * timestamps are the caller's; the log still assigns `seq`.
   */
  import(drafts: EventDraft[]): Promise<VirtaEvent[]> {
    return this.serialize(async () => {
      if (!this.loaded)
        throw new Error('virta: load() the store before writing to it')
      if (drafts.length === 0) return []
      const events = await this.logRef.push(drafts)
      await this.replayAfterPush(events)
      return events
    })
  }

  /**
   * Many writes as one (board #2408). `run` makes them against a scratch
   * copy of this board — a store over a memory log holding this log's
   * events — so each write sees the ones before it, with every guard and
   * status rule it has alone. Then the copy's new events go to the log:
   * nothing is written unless `run` finished, and nothing at all when `dry`.
   * `groups` says which events belong together (one op's); a group is
   * never split across pushes, and pushes carry up to `perPush` events —
   * the host's atomic commit limit — so a failure partway is reported at a
   * group boundary: `landed` groups are in the log, the rest are not.
   */
  transaction<T>(
    run: (scratch: VirtaStore, scratchLog: MemoryLog) => Promise<T>,
    options: {
      dry?: boolean
      perPush?: number
      groups?: (events: VirtaEvent[]) => VirtaEvent[][]
    } = {}
  ): Promise<{ result: T; events: VirtaEvent[] }> {
    return this.serialize(async () => {
      if (!this.loaded)
        throw new Error('virta: load() the store before writing to it')
      const copy = new MemoryLog()
      const held: VirtaEvent[] = []
      await replay(this.logRef, 0, (events) => void held.push(...events))
      await copy.append(held)
      const scratch = new VirtaStore({
        log: copy,
        identity: this.identity,
        ids: this.ids,
        clock: this.clock,
      })
      await scratch.load()
      const before = copy.events.length
      const result = await run(scratch, copy)
      const made = copy.events.slice(before)
      if (options.dry || made.length === 0) return { result, events: made }
      const perPush = options.perPush ?? Infinity
      const groups = options.groups ? options.groups(made) : [made]
      const pushed: VirtaEvent[] = []
      let batch: VirtaEvent[] = []
      let landed = 0
      let pending = 0
      const flush = async () => {
        if (batch.length === 0) return
        const drafts = batch.map(({ seq: _seq, ...draft }) => {
          void _seq
          return draft as EventDraft
        })
        try {
          pushed.push(...(await this.logRef.push(drafts)))
        } catch (cause) {
          throw new PartialTransactionError(landed, groups.length, cause)
        }
        landed += pending
        batch = []
        pending = 0
      }
      for (const group of groups) {
        if (batch.length && batch.length + group.length > perPush) await flush()
        batch.push(...group)
        pending++
      }
      await flush()
      await this.replayAfterPush(pushed)
      return { result, events: pushed }
    })
  }

  // ── internals ────────────────────────────────────────────────────────────

  private require(taskId: string): Task {
    const task = this.fold.tasks.get(taskId)
    if (!task) throw new Error(`virta: no task ${taskId}`)
    return task
  }

  /**
   * The invariant behind every write: the log is the truth. A commit pushes,
   * then replays from the cursor, so state is only ever built from what the
   * log returns in order — never from the push result. One queue serializes
   * loads and commits so nothing interleaves.
   */
  private commit(build: Intent[] | (() => Intent[])): Promise<VirtaEvent[]> {
    return this.serialize(async () => {
      if (!this.loaded)
        throw new Error('virta: load() the store before writing to it')
      // Intents are built INSIDE the queue so a no-op guard ("already closed",
      // "already carries that tag") reads state no concurrent write can change.
      const built = typeof build === 'function' ? build() : build
      if (built.length === 0) return []
      // a parent follows its parts, in the same commit (board #755)
      const intents = this.withParents(built)
      const drafts = intents.map(
        (intent) =>
          ({
            ...intent,
            id: this.ids(),
            at: this.clock(),
            by: this.identity,
          } as EventDraft)
      )
      const events = await this.logRef.push(drafts)
      await this.replayAfterPush(events)
      return events
    })
  }

  /**
   * The parent rule (board #755; owner, 2026-09-30: a parent is as
   * complete as its least complete child, automatically). Applied to every
   * commit, so no surface can get it half right:
   *
   * - a status change, close or reopen on a task that has counting parts
   *   is refused — it follows them — with which parts hold it back;
   * - `parent:#n` must name an existing task, not itself or a descendant;
   * - each parent a write touches gets the status of its least complete
   *   counting part, closing when that is done and reopening when it no
   *   longer is, and so on up the chain — attributed to this writer.
   */
  private withParents(built: Intent[]): Intent[] {
    const statusy = (i: Intent) => {
      const tag = tagOf(i)
      return (
        i.kind === 'closed' ||
        i.kind === 'reopened' ||
        ((i.kind === 'tagged' || i.kind === 'untagged') &&
          !!tag?.startsWith('status:'))
      )
    }
    const parenty = (i: Intent) =>
      i.kind === 'created'
        ? parentNumOf((i.payload as { tags?: string[] }).tags ?? []) !== null
        : (i.kind === 'tagged' || i.kind === 'untagged') &&
          !!tagOf(i)?.startsWith(`${PARENT_KEY}:`)
    if (!built.some((i) => statusy(i) || parenty(i))) return built
    const tasks = this.fold.tasks
    // a parent's status is its parts': not set by hand
    for (const i of built) {
      const t = tasks.get(i.taskId)
      if (!t || !statusy(i)) continue
      const holding = childrenOf(tasks.values(), t).filter((k) =>
        counts(statusValue(k.tags))
      )
      if (!holding.length) continue
      const least = leastComplete(holding.map((k) => statusValue(k.tags)))!
      const behind = holding
        .filter((k) => statusValue(k.tags) === least)
        .slice(0, 4)
        .map((k) => `#${k.num}`)
      throw new Error(
        `#${t.num} follows its ${holding.length} part(s): it is ${
          least || 'in the inbox'
        } because ${behind.join(', ')} ${
          behind.length === 1 ? 'is' : 'are'
        }. Move or finish a part and it follows (virta list "parent:#${
          t.num
        }"), or move its unfinished parts with it: virta parts #${
          t.num
        } <status>`
      )
    }
    const out = [...built]
    const tagsNow = (t: Task) => afterIntents(t, out)
    // a new parent tag names a real task, and makes no loop
    for (const i of built) {
      const tag = tagOf(i)
      if (i.kind !== 'tagged' || !tag?.startsWith(`${PARENT_KEY}:`)) continue
      const n = parentNumOf([tag])
      const target = n === null ? undefined : this.byNum(n)
      if (!target)
        throw new Error(`${tag}: a parent is named by number, parent:#12`)
      if (target.id === i.taskId)
        throw new Error('a task cannot be its own parent')
      const child = tasks.get(i.taskId)
      for (
        let up: Task | undefined = target, hops = 0;
        up && child && hops < 100;
        hops++
      ) {
        const next = parentNumOf(up.id === child.id ? [] : tagsNow(up))
        if (next === child.num)
          throw new Error(
            `#${child.num} is already above #${target.num}: a part cannot be its parent's parent`
          )
        up = next === null ? undefined : this.byNum(next)
      }
    }
    const closedNow = (t: Task) => {
      let closed = t.closedAt !== null
      for (const i of out)
        if (i.taskId === t.id) {
          if (i.kind === 'closed') closed = true
          if (i.kind === 'reopened') closed = false
        }
      return closed
    }
    // tasks this very commit creates: parts, never parents
    const fresh = out
      .filter((i) => i.kind === 'created')
      .map((i) => new Set((i.payload as { tags?: string[] }).tags ?? []))
    const partStatuses = (parent: Task): string[] => {
      const tag = `${PARENT_KEY}:#${parent.num}`
      const statuses: string[] = []
      for (const t of tasks.values()) {
        const tags = tagsNow(t)
        if (tags.has(tag)) statuses.push(statusValue(tags))
      }
      for (const tags of fresh)
        if (tags.has(tag)) statuses.push(statusValue(tags))
      return statuses
    }
    const queue: number[] = []
    for (const i of built) {
      if (!statusy(i) && !parenty(i)) continue
      const t = tasks.get(i.taskId)
      const before = t ? parentNumOf(t.tags) : null
      const after = t
        ? parentNumOf(tagsNow(t))
        : parentNumOf((i.payload as { tags?: string[] }).tags ?? [])
      for (const n of [before, after]) if (n !== null) queue.push(n)
    }
    const settled = new Set<number>()
    while (queue.length) {
      const n = queue.shift()!
      if (settled.has(n)) continue
      settled.add(n)
      const parent = this.byNum(n)
      if (!parent) continue
      const target = leastComplete(partStatuses(parent))
      if (target === null) continue
      const tags = tagsNow(parent)
      let moved = false
      if (statusValue(tags) !== target) {
        moved = true
        for (const t of tags)
          if (t.startsWith('status:'))
            out.push({
              taskId: parent.id,
              kind: 'untagged',
              payload: { tag: t },
            })
        if (target !== '')
          out.push({
            taskId: parent.id,
            kind: 'tagged',
            payload: { tag: `status:${target}` },
          })
      }
      const closed = closedNow(parent)
      if (target === 'done' && !closed) {
        moved = true
        out.push({
          taskId: parent.id,
          kind: 'closed',
          payload: { reason: 'all its parts are done' },
        })
      } else if (target !== 'done' && closed) {
        moved = true
        for (const t of tags)
          if (t.startsWith(`${CLOSED_AS_KEY}:`) || t === ARCHIVED_TAG)
            out.push({
              taskId: parent.id,
              kind: 'untagged',
              payload: { tag: t },
            })
        out.push({ taskId: parent.id, kind: 'reopened', payload: {} })
      }
      const up = parentNumOf(tags)
      if (moved && up !== null) queue.push(up)
    }
    return out
  }

  /** "Push acked, replay failed" is its own failure: the write is in the log. */
  private async replayAfterPush(pushed: VirtaEvent[]): Promise<void> {
    try {
      await this.replayNow()
    } catch (cause) {
      throw new ReplayError(pushed, cause)
    }
  }

  private serialize<T>(work: () => Promise<T>): Promise<T> {
    const run = this.queue.then(work, work)
    this.queue = run.then(
      () => undefined,
      () => undefined
    )
    return run
  }

  private async replayNow(): Promise<void> {
    // Each page is folded and announced as it lands, so a multi-page replay
    // that fails on page 2 has still shown page 1, and the cursor is honest.
    const replayed = await replay(this.logRef, this.fold.cursor, (events) => {
      const changed = new Map<string, Task>()
      let applied = 0
      for (const event of events) {
        const task = this.apply(event)
        applied++
        if (task) changed.set(task.id, task)
      }
      const last = events[events.length - 1]
      if (last && last.seq > this.fold.cursor) this.fold.cursor = last.seq
      // project events change no task; listeners still need to know something moved
      if (applied) this.notify(Array.from(changed.values()))
    })
    if (replayed > this.fold.cursor) this.fold.cursor = replayed
    this.loaded = true
  }

  /** Idempotent by `seq`: an event already at or below the cursor is ignored. */
  private apply(event: VirtaEvent): Task | undefined {
    if (event.seq <= this.fold.cursor) return undefined
    // idempotent by id as well: a re-sequenced copy must not show twice in a transcript
    if (this.fold.seen.has(event.id)) return undefined
    let list = this.eventsByTask.get(event.taskId)
    if (!list) this.eventsByTask.set(event.taskId, (list = []))
    list.push(event)
    return applyEvent(this.fold, event)
  }

  private notify(changed: Task[]): void {
    for (const listener of this.listeners) listener(changed)
  }
}
